import React, { useEffect, useRef } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import NiagaraLogo from "../../assets/Niagara.svg";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

function Header({ onLogout, sidebarCollapsed, setSidebarCollapsed, metricsData }) {
	const theme = useTheme();
	const offlineTimerRef = useRef(null);
	const inactivityTimerRef = useRef(null);

	const getPercentage = (part, total) => {
		if (typeof part !== "number" || typeof total !== "number") return "-";
		if (total === 0) return "0%";
		return ((part / total) * 100).toFixed(2) + "%";
	};

	useEffect(() => {
		const handleOnline = () => {
			console.log("Connection status: Online");
			clearTimeout(offlineTimerRef.current);
			offlineTimerRef.current = null;
		};

		const handleOffline = () => {
			console.log("Connection status: Offline");
			offlineTimerRef.current = setTimeout(
				() => {
					console.log("Logging out due to extended offline period");
					onLogout();
				},
				2 * 60 * 60 * 1000,
			);
		};

		const resetInactivityTimer = () => {
			clearTimeout(inactivityTimerRef.current);
			inactivityTimerRef.current = setTimeout(
				() => {
					console.log("Logging out due to inactivity");
					onLogout();
				},
				2 * 60 * 60 * 1000,
			);
		};

		const handleActivity = () => {
			resetInactivityTimer();
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		window.addEventListener("mousemove", handleActivity);
		window.addEventListener("keydown", handleActivity);
		window.addEventListener("touchstart", handleActivity);

		resetInactivityTimer();

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
			window.removeEventListener("mousemove", handleActivity);
			window.removeEventListener("keydown", handleActivity);
			window.removeEventListener("touchstart", handleActivity);
			clearTimeout(offlineTimerRef.current);
			clearTimeout(inactivityTimerRef.current);
		};
	}, [onLogout]);

	return (
		<AppBar
			position="static"
			sx={{
				backgroundColor: theme.palette.background.default,
				color: theme.palette.text.primary,
			}}
		>
			<Toolbar
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					backgroundColor: theme.palette.background.default,
					minHeight: "32px",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						size="small"
						sx={{ mr: 1 }}
					>
						{sidebarCollapsed ? <MenuIcon /> : <CloseIcon />}
					</IconButton>
					<img src={NiagaraLogo} alt="Your SVG" style={{ width: "4em" }} />
					<Typography
						sx={{
							marginBottom: "1em",
							color: theme.palette.text.primary,
						}}
					>
						|
					</Typography>
					<Typography
						variant="h7"
						noWrap
						component="a"
						sx={{
							fontSize: "1rem",
							fontFamily: "Poppins, sans-serif",
							marginBottom: "0.6em",
							color: theme.palette.text.primary,
						}}
					>
						Shipment Live Tracking
					</Typography>
				</Box>
				{/* Metrics Display */}
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", justifyContent: "flex-end" }}>
					{metricsData && (
						<>
							<Chip color="primary" size="small" label={`Total: ${metricsData.total ?? "-"}`} />
							<Chip
								color="primary"
								size="small"
								label={`Pending: ${metricsData.pendingPickup ?? "-"} (${getPercentage(
									metricsData.pendingPickup,
									metricsData.total,
								)})`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`Transit: ${metricsData.intransit ?? "-"} (${getPercentage(
									metricsData.intransit,
									metricsData.total,
								)})`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`On Time: ${metricsData.ontime && metricsData.late ? metricsData.ontime : "-"} (${getPercentage(
									metricsData.ontime,
									metricsData.ontime + metricsData.late + metricsData.missed,
								)})`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`On Date: ${metricsData.onDate && metricsData.onDatePercentage ? metricsData.onDate : "-"} (${metricsData.onDatePercentage}%)`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`Late: ${metricsData.late ?? "-"} (${getPercentage(
									metricsData.late,
									metricsData.total,
								)})`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`Missed: ${metricsData.missed ?? "-"} (${getPercentage(
									metricsData.missed,
									metricsData.total,
								)})`}
							/>
							<Chip
								color="primary"
								size="small"
								label={`Rescheduled: ${metricsData.rescheduled ?? "-"} (${getPercentage(
									metricsData.rescheduled,
									metricsData.total,
								)})`}
							/>
						</>
					)}
				</Box>
			</Toolbar>
		</AppBar>
	);
}

export default Header;
